function toIsoDateString(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value.toISOString();
  }
  const parsed = new Date(String(value));
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
}

function pickService(raw: unknown): Record<string, unknown> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const s = raw as Record<string, unknown>;
  const service: Record<string, unknown> = {};
  if (s._id != null) service._id = String(s._id);
  if (s.name != null) service.name = String(s.name);
  if (s.type != null) service.type = String(s.type);
  return Object.keys(service).length ? service : undefined;
}

function pickTrainer(raw: unknown): Record<string, unknown> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const t = raw as Record<string, unknown>;
  const trainer: Record<string, unknown> = {};
  if (t._id != null) trainer._id = String(t._id);
  if (t.name != null) trainer.name = String(t.name);
  return Object.keys(trainer).length ? trainer : undefined;
}

function pickTrainerPackage(raw: unknown): Record<string, unknown> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const p = raw as Record<string, unknown>;
  const pkg: Record<string, unknown> = {};
  if (p._id != null) pkg._id = String(p._id);
  const title = p.title ?? p.name ?? p.packageName;
  if (title != null) pkg.title = String(title);
  if (p.numberOfSessions != null) pkg.numberOfSessions = Number(p.numberOfSessions);
  if (p.numberOfMonths != null) pkg.numberOfMonths = Number(p.numberOfMonths);
  if (p.sessionsInterval != null) pkg.sessionsInterval = Number(p.sessionsInterval);
  if (p.subscriptionExpiry != null) pkg.subscriptionExpiry = String(p.subscriptionExpiry);
  if (p.type != null) pkg.type = String(p.type);
  if (p.packageType != null) pkg.packageType = String(p.packageType);
  if (p.startingDate != null) {
    const iso = toIsoDateString(p.startingDate);
    if (iso) pkg.startingDate = iso;
  }
  if (p.trainerShare != null) pkg.trainerShare = Number(p.trainerShare);
  return Object.keys(pkg).length ? pkg : undefined;
}

function pickFee(raw: unknown): Record<string, unknown> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const f = raw as Record<string, unknown>;
  const fee: Record<string, unknown> = {};
  if (f.paymentMode != null) fee.paymentMode = String(f.paymentMode);
  if (f.taxType != null) fee.taxType = String(f.taxType);
  if (f.taxRate != null) fee.taxRate = Number(f.taxRate);
  if (f.tax != null) fee.tax = Number(f.tax);
  if (f.fee != null) fee.fee = Number(f.fee);
  if (f.discount != null) fee.discount = Number(f.discount);
  if (f.description != null) fee.description = String(f.description);
  if (f.isSplitPayment != null) fee.isSplitPayment = Boolean(f.isSplitPayment);
  if (Array.isArray(f.splits)) {
    fee.splits = f.splits
      .filter((split) => split && typeof split === 'object')
      .map((split) => {
        const row = split as Record<string, unknown>;
        const normalized: Record<string, unknown> = {};
        if (row.paymentMode != null) normalized.paymentMode = String(row.paymentMode);
        if (row.pay != null) normalized.pay = Number(row.pay);
        if (row.tax != null) normalized.tax = Number(row.tax);
        if (row.taxRate != null) normalized.taxRate = Number(row.taxRate);
        if (row.taxType != null) normalized.taxType = String(row.taxType);
        if (row.transactionNumber != null) normalized.transactionNumber = String(row.transactionNumber);
        if (row.feeReceiptImageUrl != null) normalized.feeReceiptImageUrl = String(row.feeReceiptImageUrl);
        return normalized;
      });
  }
  return Object.keys(fee).length ? fee : undefined;
}

function pickCurrentUserData(raw: unknown): Record<string, unknown> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const u = raw as Record<string, unknown>;
  const currentUserData: Record<string, unknown> = {};
  if (u._id != null) currentUserData._id = String(u._id);
  if (u.name != null) currentUserData.name = String(u.name);
  if (u.company != null) currentUserData.company = String(u.company);
  if (u.branch != null) currentUserData.branch = String(u.branch);
  return Object.keys(currentUserData).length ? currentUserData : undefined;
}

/** Body for POST .../branches/:branchId/subscriptions (AddNewSubscriptionDto). */
export function buildAddSubscriptionPayload(raw: object): Record<string, unknown> {
  const r = (raw || {}) as Record<string, unknown>;
  const payload: Record<string, unknown> = {};

  const service = pickService(r.service);
  if (service) payload.service = service;

  const trainer = pickTrainer(r.trainer);
  if (trainer) payload.trainer = trainer;

  const trainerPackage = pickTrainerPackage(r.trainerPackage);
  if (trainerPackage) payload.trainerPackage = trainerPackage;

  const fee = pickFee(r.fee);
  if (fee) payload.fee = fee;

  const currentUserData = pickCurrentUserData(r.currentUserData);
  if (currentUserData) payload.currentUserData = currentUserData;

  const scalarFields = [
    'subscriberType',
    'initiatedFrom',
    'type',
    'subscriberId',
    'subscriberName',
    'subscriberMembership',
    'createdBy',
    'description',
    'profile',
  ] as const;

  for (const key of scalarFields) {
    const value = r[key];
    if (value === undefined || value === null || value === '') continue;
    payload[key] = String(value);
  }

  const startDate = r.startDate ?? r.subscriptionStartDate ?? r.sessionsStartDate;
  const endDate = r.endDate ?? r.subscriptionEndDate ?? r.sessionsEndDate;
  const startIso = toIsoDateString(startDate);
  const endIso = toIsoDateString(endDate);
  if (startIso) payload.startDate = startIso;
  if (endIso) payload.endDate = endIso;

  if (r.sessionsFrequency != null) {
    payload.sessionsFrequency = Number(r.sessionsFrequency);
  }

  return payload;
}
